import {
  Component,
  Input,
  OnInit,
  forwardRef,
  Injector,
  InjectFlags, ViewChild, ElementRef, ChangeDetectorRef, OnDestroy,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';

import { TimepickerModel } from './timepicker.interface';
import { TimepickerModelAdapter } from './timepicker-model-adapter';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Subscription } from 'rxjs';

@Component({
  selector: 'tr-timepicker-input',
  styleUrls: ['timepicker.component.scss'],
  templateUrl: './timepicker.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TimepickerInputComponent),
      multi: true,
    },
  ],
})
export class TimepickerInputComponent implements OnInit, ControlValueAccessor, OnDestroy {
  @ViewChild('pickerParentRef') pickerParentRef?: ElementRef;
  @Input() elementId = 'default-id-trigger';
  @Input() disabled: boolean;
  @Input() isTimepickerInvisible: boolean;
  @Input() placeholder = '12:00 PM';

  timepickerModel: string;

  showSuggestion: boolean;
  suggestedTime: string;

  pickerLeft = 0;
  pickerTop = 0;
  pickerWidth = 0;

  private timeRegexp = {
    hours: /^(0?[0-9]|1[0-2])$/,
    minutes: /^([0-5]|[0-5][0-9])$/,
    meridiem: /^([a,A,p,P]|[a,A,p,P][m, M])$/,
  };
  private previousValidHours = '';
  private previousValidMinutes = '';
  private previousValidMeridiem = '';
  private showMinutes: boolean;
  private formControl: NgControl;
  private subscription = new Subscription();

  constructor(
    private readonly injector: Injector,
    private readonly timepickerModelAdapter: TimepickerModelAdapter<any>,
    private readonly breakpointObserver: BreakpointObserver,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  get isInvalid() {
    return this.formControl?.invalid && (this.formControl?.dirty || this.formControl?.touched);
  }

  ngOnInit() {
    const orientationSub = this.breakpointObserver.observe([
      Breakpoints.HandsetPortrait,
      Breakpoints.HandsetLandscape,
      Breakpoints.TabletPortrait,
      Breakpoints.TabletLandscape,
      Breakpoints.WebPortrait,
      Breakpoints.WebLandscape,
    ])
      .subscribe(() => {
        this.showSuggestion = false;
        this.suggestedTime = '';
        this.cdr.detectChanges();
      });
    this.subscription.add(orientationSub);

    this.formControl = this.injector.get(
      NgControl,
      null,
      // tslint:disable-next-line: no-bitwise
      InjectFlags.Optional | InjectFlags.Host
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  calculateActualContentPosition(): void {
    if (!this.pickerParentRef) {
      return;
    }

    const rect = this.pickerParentRef?.nativeElement.getBoundingClientRect();
    this.pickerLeft = rect.left + window.scrollX - 8;
    this.pickerTop = rect.top + window.scrollY + rect.height;
    this.pickerWidth = rect.width;

    this.cdr.detectChanges();
  }

  noBubble(interaction: MouseEvent | TouchEvent): void {
    interaction.stopPropagation();
  }

  selectSuggested(meridiem: string) {
    const selectedTime = `${this.suggestedTime} ${meridiem}`;
    this.setModelFromView(selectedTime);
    this.showSuggestion = false;
  }

  setModelFromView(value: string) {
    this.timepickerModel = value;
    this.onChange(this.timepickerModel);
  }

  validateInput(time: string) {
    if (!time) {
      this.setModelFromView('');
      return;
    }

    const [hours, minutes = ''] = time.split(':');
    const [minute, meridiem = ''] = minutes.split(' ');
    const validatedHours =
      this.timeRegexp.hours.test(hours) || !hours ? hours : this.previousValidHours;
    const validatedMinutes =
      this.timeRegexp.minutes.test(minute) || !minute
        ? minute
        : this.previousValidMinutes;
    const validatedMeridiem =
      this.timeRegexp.meridiem.test(meridiem) || !meridiem
        ? meridiem
        : this.previousValidMeridiem;

    this.previousValidHours = validatedHours;
    this.previousValidMinutes = validatedMinutes;
    this.previousValidMeridiem = validatedMeridiem.trim();

    this.suggestTime();

    if (!time.includes('M')) {
      return;
    }

    if (
      validatedHours.length === 2 ||
      Number(validatedHours) > 1 ||
      time.indexOf(':') !== -1
    ) {
      if (time.indexOf(':') === -1 && this.showMinutes) {
        this.setModelFromView(validatedHours);
        this.showMinutes = false;
        return false;
      }
      this.showMinutes = true;
      const timepickerModel = `${validatedHours}:${validatedMinutes} ${validatedMeridiem}`.trim();
      this.setModelFromView(timepickerModel);
      return false;
    }

    this.setModelFromView(validatedHours);

    return validatedHours;
  }

  suggestTime() {
    const existedMinutes = this.previousValidMinutes ? this.previousValidMinutes : '00';
    const minutes =
      existedMinutes.length === 2 ? existedMinutes : `${this.previousValidMinutes}0`;
    const hours =
      this.previousValidHours && Number(this.previousValidHours) !== 0
        ? this.previousValidHours
        : 1;
    this.suggestedTime = `${hours}:${minutes}`;
    this.showSuggestion = true;
  }

  onblur(blurEvent: any): void {
    const formControlValue = this.formControl?.control?.value;
    // EventTarget receives relatedTarget only if any type
    if (blurEvent.relatedTarget?.classList?.contains('time-suggestions') || !formControlValue) {
      return;
    }

    let meridiem = 'AM';

    if (formControlValue instanceof Object) {
      let { hours } = formControlValue;
      meridiem = hours < 12 ? 'AM' : 'PM';
    } else {
      const timeValue = String(formControlValue);
      meridiem = timeValue.split(' ').pop();
    }

    const time = this.timepickerModel.split(' ').shift();
    this.suggestedTime = this.suggestedTime || time;

    const selectedTime = `${this.suggestedTime} ${meridiem}`;

    if (selectedTime === this.timepickerModel) {
      return;
    }

    this.setModelFromView(selectedTime);
    this.onTouched();
  }

  clickOutside() {
    this.showSuggestion = false;
  }

  //#region  ControlValueAccessor implementation

  writeValue(value: any | null) {
    const structValue = this.timepickerModelAdapter.fromModel(value);
    this.timepickerModel = this.formatModelToTimeString(structValue);
  }

  private onChange = (_: string) => {};

  registerOnChange(fn: (_: any) => void) {
    this.onChange = (formatted: string) => {
      const model = this.convertTimeStringToModel(formatted);
      const valueStruct = formatted == '' ? formatted : this.timepickerModelAdapter.toModel(model);
      fn(valueStruct);
    };
  }

  private onTouched = () => {};

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  // #endregion

  private formatModelToTimeString(model: TimepickerModel): string {
    if (!model || (model.hours == null && model.minutes == null)) {
      return '';
    }
    const { hours, minutes } = model || { hours: null, minutes: null };
    const meridiem = hours >= 0 && hours < 12 ? 'AM' : 'PM';
    const h = hours > 12 ? hours - 12 : hours == 0 ? 12 : hours;
    return `${h}:${minutes > 9 ? minutes : '0' + minutes} ${meridiem}`;
  }

  private convertTimeStringToModel(string: string): TimepickerModel {
    string = (string || '').trim();

    const { hours, minutes, meridiem } = this.parseToHoursMinutes(string);

    const addMeridiemHours =
      meridiem === 'PM'
        ? hours === 12
          ? 0
          : 12
        : meridiem === 'AM'
        ? hours === 12
          ? -12
          : 0
        : 0;
    const hours24Format = hours + addMeridiemHours;

    return {
      hours: hours24Format,
      minutes,
    };
  }

  private parseToHoursMinutes(str: string) {
    str = (str || '').trim();

    const [hoursStr = '', minutesWithMeridiemStr = ''] = str.split(':');
    const [minutesStr = '', meridiemStr = ''] = minutesWithMeridiemStr.split(' ');
    const hours = hoursStr.length ? parseInt(hoursStr, 10) : null;
    const meridiem = /AM/i.test(meridiemStr)
      ? 'AM'
      : /PM/i.test(meridiemStr)
      ? 'PM'
      : '';
    const minutes = minutesStr.length && meridiem.length ? parseInt(minutesStr, 10) : null;

    return { hours, minutes, meridiem };
  }
}
